import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap, withLatestFrom } from 'rxjs';
import { computed, inject } from '@angular/core';
import { TransportMapApiService } from '@/new-transport/transport-map/transport-map.api.service';
import {
	City,
	Program,
	ProgramsOutsideVehicle,
	RouteForVehicles,
	RouteStop,
} from '@/new-transport/_models/transport-map.model';
import { TransportSide, VehicleTransport } from '@/new-transport/_models/transport.model';
import { withOfferTransport } from '@/new-transport/_store/offer-transport.feature.store';
import { withLogger } from '@/_signal-store-features/with-signal-store-logger.feature.store';
import { assignProgramsToCities } from '@/new-transport/_utils/assign-programs-to-cities/assign-programs-to-cities';
import { deepClone } from '@/_utils/deep-clone';
import { mergeMaps } from '@/_utils/merge-two-maps';
import { appendToMap } from '@/_utils/append-to-map';
import { withVehiclesSidenav } from '@/new-transport/_store/vehicles-sidenav.feature.store';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { createTimelineArray } from '@/new-transport/_utils/create-timeline-array/create-timeline-array';
import { withLoadingFeature } from '@/_signal-store-features/loading.feature.store';
import { withBasicTransport } from '@/new-transport/_store/basic-transport.feature';
import { combineVehicleRoutesSlots } from '@/new-transport/_utils/combine-vehicle-routes-slots/combine-vehicle-routes-slots';
import { ActivatedRoute } from '@angular/router';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';

export type TransportMapState = {
	cities: City[];
	routeForVehicles: RouteForVehicles;
	takenPrograms: Map<number, Program[]>;
	programsOutsideVehicle: ProgramsOutsideVehicle[];
};

export const transportMapInitialState: TransportMapState = {
	cities: [],
	routeForVehicles: new Map(),
	takenPrograms: new Map(),
	programsOutsideVehicle: [],
};

export const TransportMapStore = signalStore(
	withState(transportMapInitialState),
	withLogger('transport map'),
	withOfferTransport(),
	withVehiclesSidenav(),
	withLoadingFeature({
		stopsIsLoading: false,
	}),
	withBasicTransport(),
	withComputed(
		({
			terms,
			transportsToLinkTerms,
			cities,
			offerTransport,
			transport,
			programsOutsideVehicle,
			routeForVehicles,
			vehicles,
		}) => ({
			destinations: computed(() => {
				const transportTermsStops = terms().map((term) => term.transportStop);
				const transportsToLinkTermsStops = transportsToLinkTerms().map((term) => term.transportStop);

				return removeDuplicatesFromArray(transportsToLinkTermsStops.concat(transportTermsStops));
			}),
			citiesWithPrograms: computed(() => {
				const stopKind = transport().side === TransportSide.DEPARTURE ? 'stopDeparture' : 'stopReturn';
				const transportTermsStops = terms().map((term) => term.transportStop);
				const transportsToLinkTermsStops = transportsToLinkTerms().map((term) => term.transportStop);
				const destinations = removeDuplicatesFromArray(transportsToLinkTermsStops.concat(transportTermsStops));

				return assignProgramsToCities(
					offerTransport(),
					cities(),
					stopKind,
					programsOutsideVehicle(),
					routeForVehicles(),
					destinations,
				);
			}),
			timeline: computed(() => createTimelineArray(routeForVehicles(), vehicles(), cities())),
		}),
	),
	withMethods((store, apiService = inject(TransportMapApiService)) => ({
		setVehicleInUse(vehicle: VehicleTransport) {
			patchState(store, (state) => ({ ...state, vehicleInUse: vehicle }));
		},
		setProgramOutsideVehicle(programsOutsideVehicle: ProgramsOutsideVehicle) {
			patchState(store, (state) => {
				const { cityId, programs } = programsOutsideVehicle;
				const updatedProgramsOutsideVehicle = state.programsOutsideVehicle.map((v) =>
					v.cityId === cityId ? { ...v, programs } : v,
				);

				const cityExists = updatedProgramsOutsideVehicle.some((v) => v.cityId === cityId);

				return {
					...state,
					programsOutsideVehicle: cityExists
						? updatedProgramsOutsideVehicle
						: [...updatedProgramsOutsideVehicle, programsOutsideVehicle],
				};
			});
		},
		appendProgramToTakenPrograms(programsToTake: Program[], vehicleId: number, override: boolean) {
			let programs = deepClone(store.takenPrograms().get(vehicleId) || []);

			if (override) {
				programs = programsToTake;
			} else {
				programsToTake.forEach((program) => {
					const alreadyExists = programs.find((p) => p.destinationId === program.destinationId);
					if (alreadyExists) {
						const index = programs.findIndex((p) => p.destinationId === program.destinationId);
						programs[index].reservation = mergeMaps(program.reservation, programs[index].reservation);
					} else {
						programs = [...programs, program];
					}
				});
			}

			patchState(store, (state) => ({ ...state, takenPrograms: state.takenPrograms.set(vehicleId, programs) }));
		},
		clearTakenProgramsForVehicle(vehicleId: number) {
			patchState(store, (state) => ({
				...state,
				takenPrograms: new Map(Array.from(state.takenPrograms).filter(([key]) => key !== vehicleId)),
			}));
		},
		setVehicleRoute(payload: RouteStop) {
			patchState(store, (state) => {
				const updatedRoute = appendToMap(state.routeForVehicles, payload.vehicleId, payload);
				return { ...state, routeForVehicles: combineVehicleRoutesSlots(deepClone(updatedRoute)) };
			});
		},
		patchWholeState(value: TransportMapState) {
			patchState(store, () => value);
		},
		removeVehicleRelatedObjects(vehicleId: number) {
			patchState(store, (state) => {
				state.takenPrograms.delete(vehicleId);
				state.routeForVehicles.delete(vehicleId);
				return {
					takenPrograms: state.takenPrograms,
					routeForVehicles: state.routeForVehicles,
					programsOutsideVehicle: state.programsOutsideVehicle.filter(
						(vehicle) => vehicle.vehicleId !== vehicleId,
					),
				};
			});
		},
		fetchStops: rxMethod<void>(
			pipe(
				tap(() => store.changeLoadingState('stops', true)),
				withLatestFrom(inject(ActivatedRoute).queryParams),
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				switchMap(([_, queryParams]) =>
					apiService.getStops(queryParams['transportKind'] ?? OfferTransportKind.COACH).pipe(
						tapResponse({
							next: (response) => patchState(store, (state) => ({ ...state, cities: response })),
							error: () => {
								throw new Error('Wystąpił problem przy pobieraniu przystanków');
							},
							finalize: () => store.changeLoadingState('stops', false),
						}),
					),
				),
			),
		),
	})),
	withHooks({
		onInit({ fetchStops }) {
			fetchStops();
		},
	}),
);
